import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Wand2, Bookmark, ImageIcon, RefreshCw, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

const L = {
  it: { label: "Primi passi", title: "Costruisci la tua Prompt Library in 4 passi", done: "{n} di 4 completati", hide: "Nascondi",
    prompt: ["Crea il tuo primo prompt", "Rispondi al questionario qui sotto"], save: ["Salvalo nella Libreria", "Aggiungilo a una lista"],
    generate: ["Genera un'immagine", "Trasforma il prompt in immagine"], edit: ["Modificala e rigenerala", "Usa l'immagine come riferimento e cambia la scena"] },
  en: { label: "Getting started", title: "Build your Prompt Library in 4 steps", done: "{n} of 4 done", hide: "Hide",
    prompt: ["Create your first prompt", "Answer the questionnaire below"], save: ["Save it to your Library", "Add it to a list"],
    generate: ["Generate an image", "Turn the prompt into an image"], edit: ["Edit and regenerate it", "Use the image as reference and change the scene"] },
  es: { label: "Primeros pasos", title: "Crea tu Prompt Library en 4 pasos", done: "{n} de 4 completados", hide: "Ocultar",
    prompt: ["Crea tu primer prompt", "Responde al cuestionario de abajo"], save: ["Guárdalo en la Biblioteca", "Añádelo a una lista"],
    generate: ["Genera una imagen", "Convierte el prompt en imagen"], edit: ["Modifícala y regenérala", "Usa la imagen como referencia y cambia la escena"] },
  fr: { label: "Premiers pas", title: "Construisez votre Prompt Library en 4 étapes", done: "{n} sur 4 terminées", hide: "Masquer",
    prompt: ["Créez votre premier prompt", "Répondez au questionnaire ci-dessous"], save: ["Enregistrez-le dans la Bibliothèque", "Ajoutez-le à une liste"],
    generate: ["Générez une image", "Transformez le prompt en image"], edit: ["Modifiez-la et régénérez-la", "Utilisez l'image comme référence et changez la scène"] },
  de: { label: "Erste Schritte", title: "Baue deine Prompt Library in 4 Schritten", done: "{n} von 4 erledigt", hide: "Ausblenden",
    prompt: ["Erstelle deinen ersten Prompt", "Beantworte den Fragebogen unten"], save: ["In der Bibliothek speichern", "Zu einer Liste hinzufügen"],
    generate: ["Erzeuge ein Bild", "Verwandle den Prompt in ein Bild"], edit: ["Bearbeiten und neu generieren", "Nutze das Bild als Referenz und ändere die Szene"] },
};

const STEPS = [
  { key: "prompt", Icon: Wand2, to: null },
  { key: "save", Icon: Bookmark, to: "/app/history" },
  { key: "generate", Icon: ImageIcon, to: "/app/studio" },
  { key: "edit", Icon: RefreshCw, to: "/app/gallery" },
];

export default function OnboardingChecklist() {
  const { lang } = useI18n();
  const tt = L[lang] || L.en;
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/account/onboarding").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data || data.dismissed) return null;
  const doneCount = STEPS.filter((s) => data.steps[s.key]).length;
  if (doneCount === STEPS.length) return null;

  const dismiss = () => { setData({ ...data, dismissed: true }); api.post("/account/onboarding/dismiss").catch(() => {}); };

  return (
    <div className="mb-8 rounded-2xl border border-white/10 bg-[#121218] p-5 md:p-6" data-testid="onboarding-checklist">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-1">{tt.label}</div>
          <div className="text-lg font-black text-[#F4EFEB]">{tt.title}</div>
          <div className="text-xs text-zinc-500 mt-1" data-testid="onboarding-progress-text">{tt.done.replace("{n}", doneCount)}</div>
        </div>
        <button onClick={dismiss} className="text-zinc-500 hover:text-white transition-colors p-1" aria-label={tt.hide} data-testid="onboarding-dismiss-btn">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="h-1 rounded-full bg-white/10 mb-5 overflow-hidden">
        <div className="h-full bg-orange-500 transition-[width] duration-500" style={{ width: `${(doneCount / STEPS.length) * 100}%` }} />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {STEPS.map(({ key, Icon, to }, i) => {
          const done = data.steps[key];
          const inner = (
            <div className={`h-full rounded-xl border p-3.5 transition-colors ${done ? "border-emerald-500/30 bg-emerald-500/[0.05]" : "border-white/10 bg-white/[0.02] hover:border-orange-500/40"}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black ${done ? "bg-emerald-500 text-black" : "bg-white/10 text-zinc-300"}`}>
                  {done ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : i + 1}
                </span>
                <Icon className="w-4 h-4 text-orange-400" />
              </div>
              <div className={`text-sm font-bold ${done ? "text-zinc-400 line-through" : "text-white"}`}>{tt[key][0]}</div>
              <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1">{tt[key][1]} {!done && to && <ArrowRight className="w-3 h-3" />}</div>
            </div>
          );
          return to && !done
            ? <Link key={key} to={to} data-testid={`onboarding-step-${key}`}>{inner}</Link>
            : <div key={key} data-testid={`onboarding-step-${key}`}>{inner}</div>;
        })}
      </div>
    </div>
  );
}
